import { valueIsEmpty } from '@wkda/funnel-utils';
import pick from 'lodash-es/pick';
import { flowToArray } from '../../FormsStateMachine/helpers/parseFlowToMachine';
import { findGraphInitialItems } from '../../FormsStateMachine/helpers/graph';
export function useFormQuestionnaireProps(flow) {
  const questionnaireItems = Object.fromEntries(Object.entries(flow).filter(_ref => {
    let [_, value] = _ref;
    return isQuestionnaireField(value);
  }));
  const questionnaireFieldsNames = flowToArray(questionnaireItems).map(value => {
    return value.name;
  });
  return {
    createSteps: [function questionnaireSteps(_ref2) {
      let [step1] = _ref2;
      // @ts-expect-error
      return [step1?.concat(questionnaireFieldsNames) ?? []];
    }],
    questionnaireMappers: [function mapQuestionnaires(_ref3) {
      let {
        values
      } = _ref3;
      return pick(values, questionnaireFieldsNames);
    }],
    createCustomValidators: [function questionnaireValidators(_ref4) {
      let {
        validators
      } = _ref4;
      let questionnaireValidators = {};
      const initialFields = Array.from(findGraphInitialItems(questionnaireItems).values());

      // Get all questionnaire fields for validation
      const allQuestionnaireFields = Object.values(questionnaireItems);
      for (const current of allQuestionnaireFields) {
        const currentFieldName = current.name;
        questionnaireValidators[currentFieldName] = function validator(_ref5) {
          let {
            values,
            errorMessages
          } = _ref5;
          // Get all currently reachable fields
          const reachableFields = getReachableFields(values, allQuestionnaireFields, initialFields);

          // If this field is not reachable, skip validation
          if (!reachableFields.has(currentFieldName)) {
            return true;
          }

          // Field is reachable - validate it
          if (valueIsEmpty(values[currentFieldName])) {
            return errorMessages.selectAnswer;
          }
          return true;
        };
      }
      return {
        ...validators,
        ...questionnaireValidators
      };
    }]
  };
}

/**
 * Type guard to check if a flow field is a questionnaire field.
 *
 * @param field - The flow field to check
 * @returns True if the field is a questionnaire field, false otherwise
 */
function isQuestionnaireField(field) {
  return field.nodeType === 'questionnaireField';
}

/**
 * Determines which questionnaire fields are currently reachable based on user responses.
 *
 * A field is considered reachable if:
 * - It's an initial field (not referenced by any other field), OR
 * - It can be reached through the flow based on current form values
 *
 * @param values - Current form values keyed by field name
 * @param allQuestionnaireFields - Array of all questionnaire fields in the flow
 * @param initialFields - Array of initial/entry point fields in the questionnaire
 *
 * @returns Set of field names that are currently reachable
 *
 * @example
 * // For a select field with guards:
 * // field1 (select) -> field2 (if value="yes") or field3 (if value="no")
 * const values = { field1: "yes" };
 * const reachable = getReachableFields(values, fields, initialFields);
 * // Returns: Set { "field1", "field2" }
 */
export function getReachableFields(values, allQuestionnaireFields, initialFields) {
  const reachable = new Set();
  const toProcessQueue = [];
  const questionnaireFieldsMap = new Map(allQuestionnaireFields.map(f => [f.name, f]));

  // Add initial fields to processing queue
  initialFields.forEach(field => {
    reachable.add(field.name);
    toProcessQueue.push(field);
  });

  // Process fields recursively
  while (toProcessQueue.length > 0) {
    const currentField = toProcessQueue.shift();
    const currentValue = values[currentField.name];
    if (currentField.inputFieldType === 'select' && currentField.guards) {
      // For select fields, check guards
      const entryGuards = Object.entries(currentField.guards);
      for (const [nextFieldName, allowedValues] of entryGuards) {
        if (allowedValues.includes(currentValue)) {
          if (!reachable.has(nextFieldName)) {
            reachable.add(nextFieldName);
            const nextField = questionnaireFieldsMap.get(nextFieldName);
            if (nextField) {
              toProcessQueue.push(nextField);
            }
          }
        }
      }
    } else if (currentField.inputFieldType === 'text') {
      // For text fields, all next fields are reachable if current field has value
      if (!valueIsEmpty(currentValue) && currentField.next) {
        for (const nextFieldName of currentField.next) {
          if (!reachable.has(nextFieldName)) {
            reachable.add(nextFieldName);
            const nextField = questionnaireFieldsMap.get(nextFieldName);
            if (nextField) {
              toProcessQueue.push(nextField);
            }
          }
        }
      }
    }
  }
  return reachable;
}